import React, { useMemo, useState, useRef, useCallback } from 'react';

/**
 * SkillMeter.jsx
 *
 * Student-facing Visual Evidence component.
 *
 * Final default UX:
 * - Current Test | Trend
 * - Strength sorting (default, no toggle)
 * - Sections → Areas → Topics
 * - No Type A/B/C in default view
 * - No visible Strength Score / Conversion / Leakage columns
 * - Details stay in hover/tap text
 *
 * Critical rule:
 * - Current section strength uses percentile/cutoff reality.
 * - Trend section view must use section_history. Do not aggregate topic_history upward and call it section trend.
 * - Trend area/topic views can use topic_history aggregation.
 */

const SECTION_NAMES = ['VARC', 'DILR', 'QA'];
const SEC_KEY = { VARC: 'varc', DILR: 'dilr', QA: 'qa' };

const AREA_SHORT = {
  'Reading Comprehension': 'RC',
  'Verbal Ability': 'VA',
  'Verbal Reasoning': 'VA',
  'Data Interpretation': 'DI',
  'Logical Reasoning': 'LR',
  Numbers: 'Num',
  Arithmetic: 'Arith',
  Algebra: 'Alg',
  Geometry: 'Geo',
  'Modern Math': 'Mod Math',
};

const sn = (v, fb = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
};

const normalizeArea = (name) => {
  if (!name) return 'Unknown';
  return name === 'Verbal Reasoning' ? 'Verbal Ability' : name;
};

const shortLabel = (name) => AREA_SHORT[name] || name;
const ratio = (a, b) => (b > 0 ? a / b : 0);
const pctLabel = (v, d = 0) => (Number.isFinite(v) ? `${(v * 100).toFixed(d)}%` : '—');
const percentileLabel = (v) => (Number.isFinite(v) ? `${v.toFixed(1)}%ile` : '—');
const round1 = (n) => (Number.isFinite(n) ? Number(n.toFixed(1)) : 0);

function accuracy(correct, wrong) {
  return correct + wrong > 0 ? correct / (correct + wrong) : 0;
}

function conversion(correct, total) {
  return total > 0 ? correct / total : 0;
}

function strengthScore(conv, share) {
  return conv * Math.sqrt(Math.max(0, share || 0));
}

function sampleBadge(node) {
  if (!node || node.level === 'section') return null;
  if (node.total <= 1) return 'low sample';
  if (node.share < 0.15) return 'small share';
  if (node.attemptRate < 0.35) return 'low attempt';
  return null;
}

function segmentTooltip(node, segment) {
  const total = sn(node.total);
  const appearanceLine = node.appearedTests ? `\nAppeared in ${node.appearedTests}/5 past tests` : '';
  if (segment === 'correct') {
    return [node.label, `${node.correct} correct`, `${pctLabel(ratio(node.correct, total))} of ${total} questions`].join('\n') + appearanceLine;
  }
  if (segment === 'wrong') {
    return [node.label, `${node.wrong} wrong`, `${pctLabel(ratio(node.wrong, total))} of ${total} questions`].join('\n') + appearanceLine;
  }
  return [node.label, `${node.skipped} skipped`, `${pctLabel(ratio(node.skipped, total))} of ${total} questions`].join('\n') + appearanceLine;
}

function sectionTrendPointTooltip(s) {
  return [
    s.test_name || 'Test',
    `Percentile: ${percentileLabel(s.percentile)}`,
    Number.isFinite(s.score) ? `Score: ${s.score}` : null,
    s.status ? `Status: ${s.status}` : null,
  ].filter(Boolean).join('\n');
}

function sectionTrendSummaryTooltip(node) {
  const series = node.percentileSeries || [];
  const latest = series.length > 0 ? series[series.length - 1] : null;
  const avg = node.avgPercentile;
  const lines = [node.label];
  if (Number.isFinite(latest?.percentile)) {
    lines.push(`Latest: ${percentileLabel(latest.percentile)}`);
  }
  if (Number.isFinite(avg)) {
    lines.push(`Average: ${percentileLabel(avg)} across ${series.length} tests`);
    if (Number.isFinite(latest?.percentile)) {
      const diff = latest.percentile - avg;
      const arrow = diff > 0 ? '↑' : diff < 0 ? '↓' : '—';
      lines.push(`vs avg: ${arrow} ${diff > 0 ? '+' : ''}${diff.toFixed(1)}%ile`);
    }
  }
  lines.push(`Cutoff: Cleared ${node.cleared || 0}/${node.tests || 0} tests`);
  return lines.join('\n');
}

function getSectionPerf(stats, sec) {
  const key = SEC_KEY[sec];
  const progressionSection = stats?.progression?.sections?.[key] || {};
  const currentSection = stats?.current_performance?.sections?.[key] || {};

  // Use progression only when marks-based cutoff fields are fully resolved.
  // Otherwise fall back to current_performance (e.g. audit dumps where cutoffs weren't resolved).
  const progressionResolved =
    progressionSection.student_facing_status &&
    progressionSection.student_facing_status !== 'UNKNOWN' &&
    progressionSection.student_facing_cutoff_marks != null;

  const p = progressionResolved ? progressionSection : currentSection;

  return {
    score: sn(p.score ?? p.marks_scored, null),
    totalMarks: sn(p.total_marks, null),
    percentile: sn(p.percentile, null),
    cutoff: sn(p.student_facing_cutoff_marks ?? p.cutoff ?? p.non_blacki_cutoff, null),
    gapText: p.student_facing_gap_text || p.gap_text || '',
    status: p.student_facing_status || p.status || p.active_status || '',
  };
}

function enrichNode(node, parentTotal) {
  node.attemptRate = ratio(node.attempted, node.total);
  node.accuracy = accuracy(node.correct, node.wrong);
  node.conversion = conversion(node.correct, node.total);
  node.share = ratio(node.total, parentTotal || node.total || 1);
  node.strength = strengthScore(node.conversion, node.share);

  // Current section rank should not use composition.
  node.sectionRankStrength =
    node.level === 'section' && Number.isFinite(node.percentile)
      ? node.percentile / 100
      : node.strength;

  return node;
}

function buildCurrentNodes(stats) {
  const sections = [];
  const areasBySection = {};
  const topicsByArea = {};

  SECTION_NAMES.forEach((sec) => {
    const key = SEC_KEY[sec];
    const sa = stats?.section_analysis?.[key];
    const areaBreakdown = sa?.area_breakdown || {};
    const topicInsights = sa?.topic_insights || {};
    const secPerf = getSectionPerf(stats, sec);

    const sectionNode = {
      id: sec,
      label: sec,
      level: 'section',
      section: sec,
      total: 0,
      attempted: 0,
      correct: 0,
      wrong: 0,
      skipped: 0,
      ...secPerf,
      children: [],
    };

    areasBySection[sec] = [];

    Object.entries(areaBreakdown).forEach(([rawArea, areaData]) => {
      const area = normalizeArea(rawArea);
      const areaNode = {
        id: `${sec}|${area}`,
        label: area,
        shortLabel: shortLabel(area),
        level: 'area',
        section: sec,
        area,
        total: 0,
        attempted: 0,
        correct: 0,
        wrong: 0,
        skipped: 0,
        children: [],
      };

      Object.entries(areaData?.by_topic || {}).forEach(([topicName, t]) => {
        const total = sn(t.total);
        const attempted = sn(t.attempted);
        const correct = sn(t.correct);
        const wrong = Math.max(0, attempted - correct);
        const skipped = Math.max(0, total - attempted);
        const insightArea = normalizeArea(topicInsights?.[topicName]?.area || area);
        if (insightArea !== area) return;

        const topicNode = {
          id: `${sec}|${area}|${topicName}`,
          label: topicName,
          level: 'topic',
          section: sec,
          area,
          topic: topicName,
          total,
          attempted,
          correct,
          wrong,
          skipped,
        };

        areaNode.total += total;
        areaNode.attempted += attempted;
        areaNode.correct += correct;
        areaNode.wrong += wrong;
        areaNode.skipped += skipped;
        areaNode.children.push(topicNode);
      });

      sectionNode.total += areaNode.total;
      sectionNode.attempted += areaNode.attempted;
      sectionNode.correct += areaNode.correct;
      sectionNode.wrong += areaNode.wrong;
      sectionNode.skipped += areaNode.skipped;
      sectionNode.children.push(areaNode);

      areasBySection[sec].push(areaNode);
      topicsByArea[areaNode.id] = areaNode.children;
    });

    sections.push(sectionNode);
  });

  sections.forEach((section) => {
    enrichNode(section, section.total || 1);
    (areasBySection[section.section] || []).forEach((area) => {
      enrichNode(area, section.total || 1);
      (topicsByArea[area.id] || []).forEach((topic) => enrichNode(topic, area.total || 1));
    });
  });

  return { sections, areasBySection, topicsByArea };
}

function buildSectionTrendNodes(stats) {
  const raw =
    stats?.section_history ||
    stats?.visual_evidence?.section_history ||
    stats?.historical_sections ||
    [];

  if (!Array.isArray(raw) || raw.length === 0) return [];

  const grouped = new Map();

  raw.forEach((r) => {
    const sec = String(r.section || r.name || '').toUpperCase();
    if (!SECTION_NAMES.includes(sec)) return;

    if (!grouped.has(sec)) {
      grouped.set(sec, {
        id: sec,
        label: sec,
        level: 'section',
        section: sec,
        total: 0,
        attempted: 0,
        correct: 0,
        wrong: 0,
        skipped: 0,
        tests: 0,
        cleared: 0,
        missed: 0,
        percentileSum: 0,
        percentileCount: 0,
        latestPercentile: null,
        latestScore: null,
        latestGapText: '',
        percentileSeries: [],
      });
    }

    const node = grouped.get(sec);
    const percentile = sn(r.percentile, null);
    const status = String(r.status || r.student_facing_status || '').toUpperCase();
    const correct = sn(r.correct);
    const wrong = sn(r.wrong);
    const skipped = sn(r.skipped);
    const attempted = sn(r.attempted, correct + wrong);
    const totalQuestions = sn(r.total_questions, attempted + skipped);

    node.tests += 1;
    node.total += totalQuestions;
    node.attempted += attempted;
    node.correct += correct;
    node.wrong += wrong;
    node.skipped += skipped;

    if (status === 'CLEARED') node.cleared += 1;
    if (status === 'MISSED') node.missed += 1;

    if (Number.isFinite(percentile)) {
      node.percentileSum += percentile;
      node.percentileCount += 1;
      node.latestPercentile = percentile;
    }

    node.latestScore = sn(r.score ?? r.marks_scored, node.latestScore);
    node.latestGapText = r.gap_text || r.student_facing_gap_text || r.gap_or_buffer_text || node.latestGapText;
    if (Number.isFinite(percentile)) {
      node.percentileSeries.push({ test_name: r.test_name || '', percentile, test_order: r.test_order ?? node.tests });
    }
  });

  return Array.from(grouped.values()).map((node) => {
    node.avgPercentile = node.percentileCount ? node.percentileSum / node.percentileCount : null;
    // Sort DESCENDING by test_order: oldest (highest test_order) first, newest (test_order=0) last
    // This makes sparkline go left=oldest → right=newest (correct chronological)
    node.percentileSeries.sort((a, b) => (b.test_order ?? 0) - (a.test_order ?? 0));
    // latestPercentile = element with test_order=0 (current/newest test)
    node.latestPercentile = node.percentileSeries.length > 0
      ? node.percentileSeries[node.percentileSeries.length - 1].percentile
      : null;
    node.attemptRate = ratio(node.attempted, node.total);
    node.accuracy = accuracy(node.correct, node.wrong);
    node.conversion = conversion(node.correct, node.total);
    node.share = 1;
    node.strength = Number.isFinite(node.latestPercentile) ? node.latestPercentile / 100 : node.conversion;
    node.sectionRankStrength = node.strength;
    return node;
  });
}

function buildTrendNodes(stats) {
  const sections = buildSectionTrendNodes(stats);
  const areasBySection = {};
  const topicsByArea = {};

  // Use area_history_all for area trend (full history, not just current-test issues)
  const areaHistory = Array.isArray(stats?.area_history_all) ? stats.area_history_all : [];
  const topicHistoryAll = Array.isArray(stats?.topic_history_all) ? stats.topic_history_all : [];

  SECTION_NAMES.forEach((sec) => {
    const areaMap = new Map();

    // Build area nodes from area_history_all
    areaHistory.filter((r) => String(r.section || '').toUpperCase() === sec).forEach((r) => {
      const area = normalizeArea(r.area);
      if (!areaMap.has(area)) {
        areaMap.set(area, {
          id: `${sec}|${area}`,
          label: area,
          shortLabel: shortLabel(area),
          level: 'area',
          section: sec,
          area,
          total: 0, attempted: 0, correct: 0, wrong: 0, skipped: 0,
          testsSet: new Set(),
          topicMap: new Map(),
        });
      }
      const areaNode = areaMap.get(area);
      areaNode.total += sn(r.total_questions);
      areaNode.attempted += sn(r.attempted);
      areaNode.correct += sn(r.correct);
      areaNode.wrong += sn(r.wrong);
      areaNode.skipped += sn(r.skipped);
      if (r.test_id || r.test_name) areaNode.testsSet.add(r.test_id || r.test_name);
    });

    // Build topic nodes from topic_history_all
    topicHistoryAll.filter((r) => String(r.section || '').toUpperCase() === sec).forEach((r) => {
      const area = normalizeArea(r.area);
      const topic = r.topic || 'Unknown Topic';

      if (!areaMap.has(area)) {
        areaMap.set(area, {
          id: `${sec}|${area}`,
          label: area,
          shortLabel: shortLabel(area),
          level: 'area',
          section: sec,
          area,
          total: 0, attempted: 0, correct: 0, wrong: 0, skipped: 0,
          testsSet: new Set(),
          topicMap: new Map(),
        });
      }
      const areaNode = areaMap.get(area);

      if (!areaNode.topicMap.has(topic)) {
        areaNode.topicMap.set(topic, {
          id: `${sec}|${area}|${topic}`,
          label: topic,
          level: 'topic',
          section: sec,
          area,
          topic,
          total: 0, attempted: 0, correct: 0, wrong: 0, skipped: 0,
          testsSet: new Set(),
          pattern: '',
          coaching: '',
        });
      }
      const topicNode = areaNode.topicMap.get(topic);
      topicNode.total += sn(r.total_questions);
      topicNode.attempted += sn(r.attempted);
      topicNode.correct += sn(r.correct);
      topicNode.wrong += sn(r.wrong);
      topicNode.skipped += sn(r.skipped);
      if (r.test_id || r.test_name) topicNode.testsSet.add(r.test_id || r.test_name);
    });

    const areas = Array.from(areaMap.values()).map((areaNode) => {
      areaNode.children = Array.from(areaNode.topicMap.values());
      delete areaNode.topicMap;
      topicsByArea[areaNode.id] = areaNode.children;
      return areaNode;
    });

    const parentTotal = areas.reduce((s, a) => s + a.total, 0) || 1;

    areas.forEach((area) => {
      enrichTrendNode(area, parentTotal);
      const topicParentTotal = area.total || 1;
      area.children.forEach((topic) => enrichTrendNode(topic, topicParentTotal));
    });

    areasBySection[sec] = areas;
  });

  return { sections, areasBySection, topicsByArea, areaTrendAvailable: areaHistory.length > 0, topicTrendAvailable: topicHistoryAll.length > 0 };
}

function enrichTrendNode(node, parentTotal) {
  node.attemptRate = ratio(node.attempted, node.total);
  node.accuracy = accuracy(node.correct, node.wrong);
  node.conversion = conversion(node.correct, node.total);
  node.share = ratio(node.total, parentTotal);
  node.strength = strengthScore(node.conversion, node.share);
  node.appearedTests = node.testsSet?.size || 0;
  node.leakagePerTest = node.appearedTests ? round1(((node.wrong + node.skipped) * 3) / node.appearedTests) : 0;
}

function rankScore(node) {
  if (node.level === 'section') return node.sectionRankStrength ?? node.strength ?? 0;
  return node.strength || 0;
}

function sortNodes(rows) {
  return [...rows].sort((a, b) => {
    const d = rankScore(b) - rankScore(a);
    if (d) return d;
    if ((b.total || 0) !== (a.total || 0)) return (b.total || 0) - (a.total || 0);
    return String(a.label).localeCompare(String(b.label));
  });
}

const CSS = `
.vemap{--ink:#111827;--muted:#687385;--green:#1f9d55;--red:#e5484d;--skip:#eef2f7;--skipline:#cbd5e1;--blue:#2563eb;--orange:#f97316;font-family:'IBM Plex Sans',Inter,ui-sans-serif,system-ui,sans-serif;color:var(--ink);font-size:14px}.vemap *{box-sizing:border-box}.ve-title{font-weight:900;font-size:18px;letter-spacing:-.01em}.ve-sub{color:var(--muted);font-size:13px;font-weight:600;margin-top:3px;line-height:1.45}.ve-controls{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin:10px 0 12px}.ve-toggle{display:flex;border:1px solid #d1d8e0;border-radius:999px;overflow:hidden;background:#fff}.ve-toggle button{border:0;background:#fff;color:#374151;padding:7px 12px;font-size:12px;font-weight:800;cursor:pointer;font-family:inherit}.ve-toggle button.active{background:#111827;color:#fff}.ve-crumbs{display:flex;gap:7px;align-items:center;flex-wrap:wrap;margin:8px 0 12px;color:var(--muted);font-size:12px;font-weight:700}.ve-crumb{border:1px solid #d1d8e0;background:#fff;color:#374151;border-radius:999px;padding:5px 10px;cursor:pointer;font-family:inherit;font-weight:800;font-size:12px}.ve-crumb.active{background:#111827;color:#fff;border-color:#111827}.ve-legend{display:flex;gap:12px;flex-wrap:wrap;color:#374151;font-size:11px;margin-bottom:8px}.ve-leg{display:flex;align-items:center;gap:5px}.ve-dot{width:12px;height:12px;border-radius:3px;display:inline-block;border:1px solid rgba(0,0,0,.08)}.ve-dot.skip{background:repeating-linear-gradient(135deg,var(--skip) 0 5px,var(--skipline) 5px 7px,var(--skip) 7px 11px)}.ve-headrow{display:grid;grid-template-columns:210px 1fr;gap:10px}.ve-row{display:grid;grid-template-columns:210px 1fr;gap:10px}.ve-headrow{color:var(--muted);font-size:12px;font-weight:900;text-transform:uppercase;letter-spacing:.04em;border-bottom:1px solid #eef2f7;padding-bottom:6px;text-align:center}.ve-headrow div:first-child{text-align:left}.ve-row{align-items:center;padding:12px 0;border-bottom:1px solid #f1f5f9;transition:background .12s}.ve-row:last-child{border-bottom:0}.ve-row.clickable:hover{background:#f0f5ff;border-left:3px solid #2563eb;padding-left:8px}.ve-chevron{display:inline;font-size:17px;color:#93a8c8;font-weight:900;margin-left:6px}.ve-row:hover .ve-chevron{color:#2563eb}.ve-drill-hint{font-size:11px;color:#2563eb;font-weight:800;margin-top:3px;opacity:0;transition:opacity .15s}.ve-row:hover .ve-drill-hint{opacity:1}.ve-name{font-weight:900;font-size:16px;line-height:1.25}.ve-name.clickable{cursor:pointer}.ve-name.clickable:hover{color:var(--blue)}.ve-subline{font-size:13px;color:var(--muted);font-weight:700;margin-top:2px}.ve-badges{display:flex;gap:5px;flex-wrap:wrap;margin-top:4px}.ve-badge{display:inline-flex;align-items:center;border-radius:999px;padding:2px 6px;font-size:10px;font-weight:800;background:#f8fafc;color:#475569;border:1px solid #e2e8f0}.ve-badge.warn{background:rgba(249,115,22,.1);color:#9a4d07;border-color:rgba(249,115,22,.25)}.ve-bararea{position:relative;height:24px}.ve-bar{position:relative;height:24px;border:1px solid #d1d5db;border-radius:8px;overflow:hidden;display:flex;background:#f3f4f6}.ve-sectionbar{background:#f3f4f6}.ve-sectionfill{height:100%;background:var(--blue);position:relative}.ve-seg{height:100%;position:relative;cursor:pointer}.ve-seg:hover{filter:brightness(.95)}.ve-green{background:var(--green)}.ve-red{background:var(--red)}.ve-skip{background:repeating-linear-gradient(135deg,var(--skip) 0 5px,var(--skipline) 5px 7px,var(--skip) 7px 11px)}.ve-lab{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-size:11px;font-weight:900;white-space:nowrap;pointer-events:none}.ve-lab.light{color:#fff;text-shadow:0 1px 2px rgba(0,0,0,.2)}.ve-lab.dark{color:#334155}.ve-total{font-size:13px;font-weight:900;color:#374155;white-space:nowrap;flex-shrink:0}.ve-tip{position:fixed;z-index:9999;display:none;max-width:340px;white-space:pre-line;background:#111827;color:#fff;border-radius:8px;padding:8px 10px;font-size:12px;line-height:1.45;box-shadow:0 10px 24px rgba(15,23,42,.24);pointer-events:none}.ve-empty{color:var(--muted);padding:16px 0;font-size:13px}.ve-foot{color:var(--muted);font-size:11px;margin-top:8px;line-height:1.45}
.ve-info{cursor:help;font-size:14px;color:var(--muted);opacity:0.6;transition:opacity .15s}
.ve-info:hover{opacity:1;color:var(--blue)}
.ve-section-group{border:1px solid #e5e7eb;border-radius:10px;margin-bottom:12px;overflow:hidden}
.ve-section-header{display:grid;grid-template-columns:210px 1fr auto;gap:10px;align-items:center;padding:12px;background:#f8fafc;cursor:pointer;transition:background .12s}
.ve-section-header:hover{background:#f0f5ff}
.ve-expand-btn{width:28px;height:28px;border:1px solid #d1d8e0;border-radius:6px;background:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:900;color:#667085;cursor:pointer;transition:transform .15s}
.ve-expand-btn.expanded{transform:rotate(90deg)}
.ve-areas{padding:0 12px 12px 32px}
.ve-area-row{display:grid;grid-template-columns:210px 1fr;gap:10px;align-items:center;padding:10px 0;border-bottom:1px solid #f1f5f9;cursor:pointer;transition:background .12s}
.ve-area-row:last-child{border-bottom:0}
.ve-area-row:hover{background:#f0f5ff;padding-left:8px;border-left:3px solid #2563eb}
.ve-area-name{font-size:16px;font-weight:800;color:#374151}
.ve-area-name .ve-chevron{font-size:15px;margin-left:4px}
.ve-usage-hint{font-size:13px;color:var(--muted);text-align:center;padding:4px 0 10px}
@media(max-width:760px){.ve-headrow{display:none}.ve-row{grid-template-columns:1fr;gap:5px;padding:12px 0}.ve-bararea{height:24px}.ve-controls{align-items:stretch}.ve-toggle{width:fit-content;max-width:100%}.ve-section-header{grid-template-columns:1fr auto;grid-template-areas:"title expand" "bar bar";gap:8px;padding:10px}.ve-section-header .ve-name{grid-area:title;font-size:16px}.ve-section-header .ve-bararea{grid-area:bar;width:100%}.ve-section-header .ve-expand-btn{grid-area:expand}.ve-area-row{grid-template-columns:1fr;gap:4px;padding:8px 0}.ve-area-name{font-size:15px}.ve-areas{padding:0 8px 8px 20px}.ve-usage-hint{display:none}}
`;

export default function SkillMeter({ stats, sectionFilter }) {
  const [viewMode, setViewMode] = useState('current');
  const [level, setLevel] = useState(sectionFilter ? 'area' : 'section');
  const [selectedSection, setSelectedSection] = useState(sectionFilter?.toUpperCase() || null);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set(SECTION_NAMES));
  const tipRef = useRef(null);

  const currentData = useMemo(() => buildCurrentNodes(stats), [stats]);
  const trendData = useMemo(() => buildTrendNodes(stats), [stats]);
  const data = viewMode === 'current' ? currentData : trendData;

  const showTip = useCallback((text, e) => {
    const tip = tipRef.current;
    if (!tip) return;
    tip.textContent = text;
    tip.style.display = 'block';
    const rect = tip.getBoundingClientRect();
    const targetRect = e.currentTarget?.getBoundingClientRect?.();
    const baseX = e.clientX || targetRect?.left || 0;
    const baseY = e.clientY || targetRect?.top || 0;
    let x = baseX + 12;
    let y = baseY + 12;
    if (x + rect.width > window.innerWidth - 12) x = baseX - rect.width - 12;
    if (y + rect.height > window.innerHeight - 12) y = baseY - rect.height - 12;
    tip.style.left = `${Math.max(8, x)}px`;
    tip.style.top = `${Math.max(8, y)}px`;
  }, []);

  const hideTip = useCallback(() => {
    if (tipRef.current) tipRef.current.style.display = 'none';
  }, []);

  if (!stats) return null;

  function resetToStart() {
    if (sectionFilter) {
      setLevel('area');
      setSelectedSection(sectionFilter.toUpperCase());
      setSelectedAreaId(null);
    } else {
      setLevel('section');
      setSelectedSection(null);
      setSelectedAreaId(null);
    }
  }

  function rowsForLevel() {
    if (level === 'section') return sortNodes(data.sections || []);
    if (level === 'area') return sortNodes(data.areasBySection?.[selectedSection || sectionFilter?.toUpperCase()] || []);
    if (level === 'topic') return sortNodes(data.topicsByArea?.[selectedAreaId] || []);
    return [];
  }

  const rows = rowsForLevel();
  const maxTotal = Math.max(...rows.map((r) => r.total || 0), 1);
  const sectionTrendUnavailable = viewMode === 'trend' && level === 'section' && (!data.sections || data.sections.length === 0);
  const areaTrendUnavailable = viewMode === 'trend' && level === 'area' && !data.areaTrendAvailable;
  const topicTrendUnavailable = viewMode === 'trend' && level === 'topic' && !data.topicTrendAvailable;
  const trendUnavailable = sectionTrendUnavailable || areaTrendUnavailable || topicTrendUnavailable;

  function changeViewMode(next) {
    setViewMode(next);
  }

  function toggleSection(sec) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sec)) next.delete(sec);
      else next.add(sec);
      return next;
    });
  }

  function clickRow(node) {
    if (node.level === 'section') {
      setSelectedSection(node.section);
      setLevel('area');
      setSelectedAreaId(null);
    } else if (node.level === 'area') {
      setSelectedSection(node.section);
      setSelectedAreaId(node.id);
      setLevel('topic');
    }
  }

  function rowTitle(node) {
    if (node.level === 'section') return node.label;
    if (node.level === 'area') return node.shortLabel || shortLabel(node.label);
    return node.label;
  }

  function rowSubtitle(node) {
    if (node.level === 'section' && viewMode === 'current') {
      return ''; // Percentile shown in bar, gap text shown on right — no subtitle needed
    }
    if (node.level === 'section' && viewMode === 'trend') {
      return `${node.tests || 0} tests${Number.isFinite(node.avgPercentile) ? ` · avg ${percentileLabel(node.avgPercentile)}` : ''}`;
    }
    if (node.level === 'area') return node.label;
    return ''; // Do not repeat area below topic.
  }

  function tooltip(node) {
    if (node.level === 'section' && viewMode === 'current') {
      return [
        node.label,
        `Percentile: ${Number.isFinite(node.percentile) ? percentileLabel(node.percentile) : '—'}`,
        `Score: ${Number.isFinite(node.score) ? node.score : '—'}${Number.isFinite(node.totalMarks) ? `/${node.totalMarks}` : ''}`,
        node.cutoff != null ? `Cutoff marks: ${node.cutoff}` : null,
        node.gapText ? `Gap: ${node.gapText}` : null,
        node.status ? `Status: ${node.status}` : null,
        'Section ranking uses percentile / cutoff reality, not composition.',
      ].filter(Boolean).join('\n');
    }

    if (node.level === 'section' && viewMode === 'trend') {
      return sectionTrendSummaryTooltip(node);
    }

    const badge = sampleBadge(node);
    const lines = [
      node.level === 'area' ? `${node.label} (${node.section})` : node.label,
      `${node.correct} correct, ${node.wrong} wrong, ${node.skipped} skipped`,
      `${node.total} ${viewMode === 'trend' ? 'historical appearances' : 'questions shown'}`,
      `Attempt rate: ${pctLabel(node.attemptRate)}`,
      `Accuracy: ${pctLabel(node.accuracy)}`,
      `Conversion: ${pctLabel(node.conversion)}`,
      `Share: ${pctLabel(node.share)}`,
    ];
    lines.push(`Strength = conversion × √share = ${(node.strength * 100).toFixed(1)}`);
    if (viewMode === 'trend') {
      lines.push(`Appeared in ${node.appearedTests || 0}/5 tests`);
      lines.push(`Approx leakage: ${node.leakagePerTest || 0} marks/test`);
      if (node.pattern) lines.push(`Pattern: ${node.pattern}`);
      if (node.coaching) lines.push(`Action: ${node.coaching}`);
    }
    return lines.join('\n');
  }

  return (
    <div className="vemap">
      <style>{CSS}</style>

      {/* Header: Breadcrumbs (left) + Toggle (right) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <div className="ve-crumbs" style={{ margin: 0 }}>
          {!sectionFilter && <button className={`ve-crumb ${level === 'section' ? 'active' : ''}`} onClick={resetToStart}>Sections</button>}
          {selectedSection && <>
            {!sectionFilter && <span>›</span>}
            <button className={`ve-crumb ${level === 'area' ? 'active' : ''}`} onClick={() => { setLevel('area'); setSelectedAreaId(null); }}>{selectedSection}</button>
          </>}
          {selectedAreaId && <><span>›</span><button className="ve-crumb active">{selectedAreaId.split('|')[1]}</button></>}
        </div>
        <div className="ve-toggle">
          <button className={viewMode === 'current' ? 'active' : ''} onClick={() => changeViewMode('current')}>Current Test</button>
          <button className={viewMode === 'trend' ? 'active' : ''} onClick={() => changeViewMode('trend')}>Trend</button>
        </div>
      </div>

      {sectionTrendUnavailable && <div className="ve-empty">This data is not available for this report. Try viewing reports generated for newer tests.</div>}
      {areaTrendUnavailable && <div className="ve-empty">This data is not available for this report. Try viewing reports generated for newer tests.</div>}
      {topicTrendUnavailable && <div className="ve-empty">This data is not available for this report. Try viewing reports generated for newer tests.</div>}

      {/* Usage hints — desktop only, adapts to view mode and drill level */}
      {!trendUnavailable && level === 'section' && (
        <div className="ve-usage-hint">
          {viewMode === 'current'
            ? 'Click any area to drill into topics · Toggle to compare with past tests'
            : 'Hover dots for per-test percentile · Click areas to drill into topics'}
        </div>
      )}
      {level !== 'section' && viewMode === 'trend' && !trendUnavailable && (
        <div className="ve-usage-hint">Showing current test + up to 5 past tests</div>
      )}

      {/* Expandable sections view (BOTH current test AND trend, at section level) */}
      {/* Always use currentData sections for sort order (current test percentile is authoritative) */}
      {!trendUnavailable && level === 'section' && sortNodes(currentData.sections || []).map((sectionNode) => {
        const isExpanded = expandedSections.has(sectionNode.id);
        const sourceData = viewMode === 'current' ? currentData : trendData;
        const areas = sortNodes(sourceData.areasBySection?.[sectionNode.id] || []);
        const areaMaxTotal = Math.max(...areas.map((a) => a.total || 0), 1);
        const trendSectionNode = viewMode === 'trend'
          ? (trendData.sections || []).find((s) => s.id === sectionNode.id) || sectionNode
          : sectionNode;
        return (
          <div key={sectionNode.id} className="ve-section-group">
            <div className="ve-section-header" onClick={() => toggleSection(sectionNode.id)}>
              <div className="ve-name" style={{ fontSize: '17px', fontWeight: 800 }}>
                {sectionNode.label}
                {viewMode === 'current' && sectionNode.gapText && (
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280', marginLeft: '8px' }}>
                    ({sectionNode.gapText})
                  </span>
                )}
                <span className="ve-chevron">›</span>
              </div>
              <SectionBar node={viewMode === 'trend' ? trendSectionNode : sectionNode} viewMode={viewMode} tooltip={tooltip(viewMode === 'trend' ? trendSectionNode : sectionNode)} showTip={showTip} hideTip={hideTip} />
              <button className={`ve-expand-btn ${isExpanded ? 'expanded' : ''}`}>›</button>
            </div>
            {isExpanded && (
              <div className="ve-areas">
                {areas.map((areaNode) => {
                  return (
                    <div key={areaNode.id} className="ve-area-row" onClick={() => clickRow(areaNode)}>
                      <div className="ve-area-name">
                        {areaNode.label}
                        <span className="ve-chevron">›</span>
                      </div>
                      <OutcomeBar node={areaNode} maxTotal={areaMaxTotal} tooltip={tooltip(areaNode)} showTip={showTip} hideTip={hideTip} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Standard view (area/topic levels only) */}
      {!trendUnavailable && level !== 'section' && rows.map((node) => {
        const clickable = node.level !== 'topic';
        return <div key={node.id} className={`ve-row ${clickable ? 'clickable' : ''}`}>
          <div className={`ve-name ${clickable ? 'clickable' : ''}`} onClick={() => clickable && clickRow(node)}>
            {rowTitle(node)}{clickable && <span className="ve-chevron">›</span>}
          </div>
          <OutcomeBar node={node} maxTotal={maxTotal} tooltip={tooltip(node)} showTip={showTip} hideTip={hideTip} />
        </div>;
      })}

      {!trendUnavailable && rows.length === 0 && <div className="ve-empty">No data available for this view.</div>}

      <div className="ve-tip" ref={tipRef} />
    </div>
  );
}

function SectionBar({ node, viewMode, tooltip, showTip, hideTip }) {
  if (viewMode === 'trend') {
    // Percentile sparkline SVG with per-dot tooltips
    const series = node.percentileSeries || [];
    if (series.length === 0) {
      return <div className="ve-bararea" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div className="ve-bar ve-sectionbar" style={{ flex: '1 1 auto', minWidth: 0 }}><span className="ve-lab dark">No percentile data</span></div></div>;
    }
    const w = 350, h = 28, pad = 4;
    const minP = Math.min(...series.map((s) => s.percentile));
    const maxP = Math.max(...series.map((s) => s.percentile));
    const range = maxP - minP || 1;
    const points = series.map((s, i) => {
      const x = series.length === 1 ? w / 2 : pad + (i / (series.length - 1)) * (w - 2 * pad);
      const y = h - pad - ((s.percentile - minP) / range) * (h - 2 * pad);
      return `${x},${y}`;
    }).join(' ');
    const latest = series[series.length - 1];
    const trend = Number.isFinite(node.avgPercentile) ? (latest.percentile - node.avgPercentile) : 0;
    const trendLabel = trend > 0 ? `↑ +${trend.toFixed(1)}` : trend < 0 ? `↓ ${trend.toFixed(1)}` : '→ 0.0';
    const trendColor = trend > 0 ? 'var(--green)' : trend < 0 ? 'var(--red)' : 'var(--muted)';
    const summaryTip = sectionTrendSummaryTooltip(node);
    const summaryHandlers = showTip ? {
      onMouseEnter: (e) => showTip(summaryTip, e),
      onMouseMove: (e) => showTip(summaryTip, e),
      onClick: (e) => showTip(summaryTip, e),
      onMouseLeave: hideTip,
    } : {};
    return <div className="ve-bararea" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', overflow: 'visible', flex: '1 1 auto', minWidth: 0, width: 'auto', height: '100%' }}>
        <polyline points={points} fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {series.map((s, i) => {
          const x = series.length === 1 ? w / 2 : pad + (i / (series.length - 1)) * (w - 2 * pad);
          const y = h - pad - ((s.percentile - minP) / range) * (h - 2 * pad);
          const pointTip = sectionTrendPointTooltip(s);
          const dotHandlers = showTip ? {
            onMouseEnter: (e) => { e.stopPropagation(); showTip(pointTip, e); },
            onMouseMove: (e) => { e.stopPropagation(); showTip(pointTip, e); },
            onClick: (e) => { e.stopPropagation(); showTip(pointTip, e); },
            onMouseLeave: (e) => { e.stopPropagation(); hideTip(); },
          } : {};
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="10" fill="transparent" style={{ cursor: showTip ? 'pointer' : 'default' }} {...dotHandlers} />
              <circle cx={x} cy={y} r="3" fill="var(--blue)" pointerEvents="none" />
            </g>
          );
        })}
      </svg>
      <span className="ve-total" style={{ color: trendColor, position: 'static' }} {...summaryHandlers}>{trendLabel}</span>
    </div>;
  }

  // Current test: segment-specific tooltips
  const c = ratio(node.correct, node.total) * 100;
  const w = ratio(node.wrong, node.total) * 100;
  const s = ratio(node.skipped, node.total) * 100;
  const makeSegHandlers = (segment) => {
    const tip = segmentTooltip(node, segment);
    return showTip ? {
      onMouseEnter: (e) => { e.stopPropagation(); showTip(tip, e); },
      onMouseMove: (e) => { e.stopPropagation(); showTip(tip, e); },
      onClick: (e) => { e.stopPropagation(); showTip(tip, e); },
      onMouseLeave: (e) => { e.stopPropagation(); hideTip(); },
    } : {};
  };
  return <div className="ve-bararea" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div className="ve-bar" style={{ flex: '1 1 auto', minWidth: 0 }}>
    {c > 0 && <div className="ve-seg ve-green" style={{ width: `${c}%` }} {...makeSegHandlers('correct')}><span className="ve-lab light">{node.correct}</span></div>}
    {w > 0 && <div className="ve-seg ve-red" style={{ width: `${w}%` }} {...makeSegHandlers('wrong')}><span className="ve-lab light">{node.wrong}</span></div>}
    {s > 0 && <div className="ve-seg ve-skip" style={{ width: `${s}%` }} {...makeSegHandlers('skipped')}><span className="ve-lab dark">{node.skipped}</span></div>}
  </div><span className="ve-total" style={{ position: 'static' }}>{node.total}</span></div>;
}

function OutcomeBar({ node, maxTotal, tooltip, showTip, hideTip }) {
  const c = ratio(node.correct, node.total) * 100;
  const w = ratio(node.wrong, node.total) * 100;
  const s = ratio(node.skipped, node.total) * 100;
  const makeSegHandlers = (segment) => {
    const tip = segmentTooltip(node, segment);
    return showTip ? {
      onMouseEnter: (e) => { e.stopPropagation(); showTip(tip, e); },
      onMouseMove: (e) => { e.stopPropagation(); showTip(tip, e); },
      onClick: (e) => { e.stopPropagation(); showTip(tip, e); },
      onMouseLeave: (e) => { e.stopPropagation(); hideTip(); },
    } : {};
  };

  return <div className="ve-bararea" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div className="ve-bar" style={{ flex: '1 1 auto', minWidth: 0 }}>
    {c > 0 && <div className="ve-seg ve-green" style={{ width: `${c}%` }} {...makeSegHandlers('correct')}><span className="ve-lab light">{node.correct}</span></div>}
    {w > 0 && <div className="ve-seg ve-red" style={{ width: `${w}%` }} {...makeSegHandlers('wrong')}><span className="ve-lab light">{node.wrong}</span></div>}
    {s > 0 && <div className="ve-seg ve-skip" style={{ width: `${s}%` }} {...makeSegHandlers('skipped')}><span className="ve-lab dark">{node.skipped}</span></div>}
  </div><span className="ve-total" style={{ position: 'static' }}>{node.total}</span></div>;
}
